// Per-overlay line style preferences for the map overlays that draw lines
// (official HiiKER hikes, my hikes, my recordings, completed hikes).
//
// Deliberately mirrors `routeStyleSettings.ts` in shape — colour, thickness
// multiplier, opacity — because the two are edited through the same UI and
// synced through the same `User.routeStyleSettings` JSONB column (under the
// `overlay_*` route_type keys). Kept free of DOM/bus/GraphQL imports beyond
// localStorage so Jest can exercise it directly.

export type OverlayLineType = 'officialHikes' | 'myHikes' | 'recordings' | 'completed';

export interface OverlayLinePrefs {
  color: string;
  thickness: number; // multiplier applied to the layer's base width: 0.5 | 1 | 2 | 3
  opacity: number;   // percent, 10-100
}

export type OverlayLineStyles = Record<OverlayLineType, OverlayLinePrefs>;

export const OVERLAY_LINE_STYLE_STORAGE_KEY = 'hiiker_overlay_line_style_prefs';

export const OVERLAY_LINE_TYPES: OverlayLineType[] = [
  'officialHikes',
  'myHikes',
  'recordings',
  'completed',
];

/** Defaults match the iOS/Android overlay line colours so an unsynced client
 *  renders the same as a synced one. */
export const DEFAULT_OVERLAY_LINE_STYLES: OverlayLineStyles = {
  officialHikes: { color: '#DB2B35', thickness: 1, opacity: 100 },
  myHikes:       { color: '#F57700', thickness: 1, opacity: 100 },
  recordings:    { color: '#C37098', thickness: 1, opacity: 100 },
  completed:     { color: '#005BE4', thickness: 1, opacity: 100 },
};

export const OVERLAY_LINE_LABELS: Record<OverlayLineType, string> = {
  officialHikes: 'HiiKER hikes',
  myHikes:       'My hikes',
  recordings:    'Recordings',
  completed:     'Completed',
};

/** The personal-trail vector source names used by `LineSourceHelper`. */
export const OVERLAY_LINE_TYPE_BY_SOURCE: Record<string, OverlayLineType> = {
  get_user_planned_trails:  'myHikes',
  get_user_recordings:      'recordings',
  get_user_completed_hikes: 'completed',
};

/** Vector source name → overlay line type. `null` for anything not a line overlay. */
export function overlayLineTypeForSource(src: string | null | undefined): OverlayLineType | null {
  if (!src) return null;
  return OVERLAY_LINE_TYPE_BY_SOURCE[src] ?? null;
}

function defaultsCopy(): OverlayLineStyles {
  return {
    officialHikes: { ...DEFAULT_OVERLAY_LINE_STYLES.officialHikes },
    myHikes:       { ...DEFAULT_OVERLAY_LINE_STYLES.myHikes },
    recordings:    { ...DEFAULT_OVERLAY_LINE_STYLES.recordings },
    completed:     { ...DEFAULT_OVERLAY_LINE_STYLES.completed },
  };
}

export function getOverlayLineStyles(): OverlayLineStyles {
  const next = defaultsCopy();
  try {
    const raw = localStorage.getItem(OVERLAY_LINE_STYLE_STORAGE_KEY);
    if (raw) {
      const stored = JSON.parse(raw) || {};
      for (const type of OVERLAY_LINE_TYPES) {
        next[type] = { ...next[type], ...(stored[type] || {}) };
      }
    }
  } catch {}
  return next;
}

export function saveOverlayLineStyles(styles: OverlayLineStyles): void {
  try {
    localStorage.setItem(OVERLAY_LINE_STYLE_STORAGE_KEY, JSON.stringify(styles));
  } catch {}
}

/** Returns a copy of `styles` with `type` back at its shipped default. */
export function withOverlayLineStyleReset(
  styles: OverlayLineStyles,
  type: OverlayLineType,
): OverlayLineStyles {
  return { ...styles, [type]: { ...DEFAULT_OVERLAY_LINE_STYLES[type] } };
}

/**
 * Scales a Mapbox `line-width` value by the user's thickness multiplier.
 *
 * Overlay line widths are authored two ways in this codebase: a flat number
 * (official hikes) and a zoom `interpolate` expression (personal trails). Both
 * have to respond to the multiplier, so this walks an interpolate expression's
 * output stops and leaves everything else — inputs, the interpolation type,
 * the `['zoom']` getter — untouched. Anything it doesn't recognise is returned
 * unchanged rather than corrupted into an invalid paint value.
 */
export function scaleLineWidth(spec: number | any[], multiplier: number): number | any[] {
  const factor = Number.isFinite(multiplier) && multiplier > 0 ? multiplier : 1;
  if (typeof spec === 'number') return round2(spec * factor);
  if (!Array.isArray(spec)) return spec;
  if (spec[0] !== 'interpolate') return spec;

  // ['interpolate', <type>, <input>, in0, out0, in1, out1, …] — outputs are the
  // odd-indexed entries of the stop list, which starts at index 3.
  const scaled = spec.slice();
  for (let i = 4; i < scaled.length; i += 2) {
    if (typeof scaled[i] === 'number') scaled[i] = round2(scaled[i] * factor);
  }
  return scaled;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Mapbox `line-opacity` (0–1) from the stored percent. */
export function lineOpacityFor(prefs: OverlayLinePrefs): number {
  const pct = Number.isFinite(prefs.opacity) ? prefs.opacity : 100;
  return Math.max(0, Math.min(1, pct / 100));
}
